import React from 'react';
import PartnershipGrowthShowcase from './PartnershipGrowthShowcase';

const benefitCards = [
  {
    title: 'For merchants',
    description:
      'Bring in customers from other local businesses, encourage repeat visits, and control your exposure with merchant-set limits.',
  },
  {
    title: 'For customers',
    description:
      'Use rewards, vouchers, and gift value at more than one business instead of being stuck with a single merchant.',
  },
  {
    title: 'For the local economy',
    description:
      'Keep more spending inside a connected group of local businesses instead of losing value out of the area.',
  },
];

const proofCards = [
  {
    label: 'Network overview',
    title: 'See your network',
    description:
      'Track who you are connected to, how strong your network is, and which reward relationships are active.',
    image: '/screenshots/merchant-network/01-my-tokens-overview-ipad.png',
    width: 1640,
    height: 2360,
    alt: 'My Token overview showing issued token totals, partnership network strength, and acceptance direction stats',
  },
  {
    label: 'Spend map',
    title: 'Show where rewards can be spent',
    description:
      'Customers and merchants can quickly see which nearby businesses accept a reward, making it more useful.',
    image: '/screenshots/merchant-network/05-where-to-spend-grnl-ipad.png',
    width: 1640,
    height: 2360,
    alt: 'Where to spend view filtered to a partner token across multiple nearby merchants',
  },
  {
    label: 'Partner limit',
    title: 'Set a limit and accept a partner reward',
    description:
      "Accept a partner's rewards up to a dollar amount you choose, so you stay in control as the network grows.",
    image: '/screenshots/merchant-network/11-accept-amount-20-ipad.png',
    width: 820,
    height: 1180,
    alt: 'Partnership acceptance screen confirming a new merchant token relationship with a spending cap',
  },
];

export const MerchantConnectionSection: React.FC = () => {
  return (
    <section
      id="merchant-network"
      className="bg-[linear-gradient(180deg,#fff7ef_0%,#fffaf6_100%)] px-4 py-24"
    >
      <div className="mx-auto max-w-6xl">
        <div className="max-w-4xl">
          <span className="inline-flex rounded-full bg-white px-4 py-1 text-sm font-semibold text-brand-primary shadow-sm">
            Local merchant network
          </span>
          <h2 className="mt-6 text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl">
            Accept other local merchants&apos; rewards and grow your reach without
            giving up control.
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-neutral-600">
            Once checkout is running, you choose which local merchants to
            connect with and how much of their rewards you&apos;ll accept. That
            helps bring in new customers, gives existing customers more ways to
            spend, and keeps more value moving through the local network.
          </p>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {benefitCards.map((card) => (
            <article
              key={card.title}
              className="rounded-[1.75rem] border border-brand-primary/12 bg-white px-5 py-5 shadow-[0_20px_50px_rgba(130,64,29,0.08)]"
            >
              <h3 className="text-xl font-semibold text-neutral-900">
                {card.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                {card.description}
              </p>
            </article>
          ))}
        </div>

        <div className="mt-10">
          <PartnershipGrowthShowcase
            badge="How merchant partnerships work"
            titleLevel="h3"
          />
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {proofCards.map((card) => (
            <article
              key={card.title}
              className="overflow-hidden rounded-[2rem] border border-brand-primary/12 bg-[#fffdf9] shadow-[0_24px_60px_rgba(130,64,29,0.10)]"
            >
              <div className="bg-[radial-gradient(circle_at_top,_rgba(255,236,214,0.92),_transparent_62%),linear-gradient(180deg,#fff8ef_0%,#fdf1e5_100%)] px-5 pb-5 pt-6">
                <div className="relative mx-auto max-w-[18rem]">
                  <div
                    className="absolute inset-x-5 bottom-3 top-7 rounded-[2rem] bg-[rgba(214,137,88,0.16)] blur-2xl"
                    aria-hidden="true"
                  />
                  <div
                    className="absolute inset-x-3 bottom-2 top-4 rounded-[2rem] border border-white/70 bg-[#fff5ea] shadow-[0_16px_40px_rgba(100,48,22,0.08)]"
                    style={{ transform: 'rotate(-2.5deg)' }}
                    aria-hidden="true"
                  />
                  <div
                    className="absolute inset-x-2 bottom-1 top-2 rounded-[2rem] border border-[#f0dccd] bg-white/90 shadow-[0_18px_44px_rgba(100,48,22,0.10)]"
                    style={{ transform: 'rotate(1.5deg)' }}
                    aria-hidden="true"
                  />
                  <div className="relative rounded-[2rem] border border-[#ead8c9] bg-white p-3 shadow-[0_26px_56px_rgba(78,35,14,0.14)]">
                    <div className="mb-3 flex items-center justify-between px-1">
                      <span className="inline-flex rounded-full bg-[#fff2e4] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-primary">
                        {card.label}
                      </span>
                      <div className="flex gap-1.5" aria-hidden="true">
                        <span className="h-2.5 w-2.5 rounded-full bg-[#f6c9a2]" />
                        <span className="h-2.5 w-2.5 rounded-full bg-[#edb77d]" />
                        <span className="h-2.5 w-2.5 rounded-full bg-[#d6814d]" />
                      </div>
                    </div>
                    <div className="overflow-hidden rounded-[1.45rem] border border-[#ead8c9] bg-[#fffaf4]">
                      <img
                        src={card.image}
                        alt={card.alt}
                        width={card.width}
                        height={card.height}
                        className="w-full bg-[#fffaf4]"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 pb-6 pt-2">
                <h3 className="text-2xl font-semibold text-neutral-900">
                  {card.title}
                </h3>
                <p className="mt-3 text-base leading-relaxed text-neutral-600">
                  {card.description}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MerchantConnectionSection;
